const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeSpec = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);

const normalizeString = (value) => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

const buildPlannerContextSnapshot = ({
  plannerContext,
  outcome = null,
  analysisType = null,
  instrumentSymbol = null,
  plannerSource = null,
  plannerModel = null,
  missingDetails = [],
  queryPlan = null,
  clarifyingQuestion = null,
  reviewPosture = null,
}) => {
  const normalizedPlannerContext = normalizeSpec(plannerContext) || {};

  return {
    ...normalizedPlannerContext,
    outcome: normalizeString(normalizedPlannerContext.outcome) || normalizeString(outcome),
    analysis_type: normalizeString(normalizedPlannerContext.analysis_type) || normalizeString(analysisType),
    instrument_symbol: normalizeString(normalizedPlannerContext.instrument_symbol) || normalizeString(instrumentSymbol),
    planner_source: normalizeString(normalizedPlannerContext.planner_source) || normalizeString(plannerSource),
    planner_model: normalizeString(normalizedPlannerContext.planner_model) || normalizeString(plannerModel),
    missing_details: normalizeArray(normalizedPlannerContext.missing_details).length > 0
      ? normalizeArray(normalizedPlannerContext.missing_details)
      : normalizeArray(missingDetails),
    query_plan: normalizeSpec(normalizedPlannerContext.query_plan) || normalizeSpec(queryPlan),
    clarifying_question: normalizeSpec(normalizedPlannerContext.clarifying_question) || normalizeSpec(clarifyingQuestion),
    review_posture: normalizeSpec(normalizedPlannerContext.review_posture) || normalizeSpec(reviewPosture),
  };
};

const buildActiveDraftFromSpec = ({
  sourceType,
  sourceLabel = null,
  sourceJobId = null,
  planReady = true,
  submitReady = true,
  outcome = null,
  analysisType = null,
  instrumentSymbol = null,
  plannerSource = 'deterministic_prompt_router_v1',
  plannerModel = null,
  assumptions = [],
  warnings = [],
  missingDetails = [],
  candidateAnalysisTypes = [],
  queryPlan = null,
  clarifyingQuestion = null,
  reviewPosture = null,
  plannerContext = null,
  spec,
  focusTarget = 'review',
  persistedDraftId = null,
  persistedDraftName = '',
  persistedDraftStatus = 'active',
}) => ({
  sourceType,
  sourceLabel,
  sourceJobId,
  planReady,
  submitReady,
  outcome: normalizeString(outcome) || (planReady ? 'ready' : 'clarify'),
  analysisType: normalizeString(analysisType),
  instrumentSymbol: normalizeString(instrumentSymbol),
  plannerSource,
  plannerModel,
  assumptions: normalizeArray(assumptions),
  warnings: normalizeArray(warnings),
  missingDetails: normalizeArray(missingDetails),
  candidateAnalysisTypes: normalizeArray(candidateAnalysisTypes),
  queryPlan: normalizeSpec(queryPlan),
  clarifyingQuestion: normalizeSpec(clarifyingQuestion),
  reviewPosture: normalizeSpec(reviewPosture),
  plannerContext: buildPlannerContextSnapshot({
    plannerContext,
    outcome,
    analysisType,
    instrumentSymbol,
    plannerSource,
    plannerModel,
    missingDetails,
    queryPlan,
    clarifyingQuestion,
    reviewPosture,
  }),
  spec,
  specText: spec ? formatDraftSpecText(spec) : '',
  focusTarget,
  persistedDraftId,
  persistedDraftName,
  persistedDraftStatus,
});

export const formatDraftSpecText = (value) => JSON.stringify(value, null, 2);

export const buildPlannerAnalysisDraft = (planResponse) => {
  const spec = normalizeSpec(planResponse?.draft_spec);

  return buildActiveDraftFromSpec({
    sourceType: 'planner',
    planReady: Boolean(planResponse?.plan_ready),
    submitReady: Boolean(planResponse?.submit_ready),
    outcome: planResponse?.outcome,
    analysisType: planResponse?.analysis_type,
    instrumentSymbol: planResponse?.instrument_symbol,
    plannerSource: planResponse?.planner_source || 'deterministic_prompt_router_v1',
    plannerModel: planResponse?.planner_model || null,
    assumptions: normalizeArray(planResponse?.assumptions),
    warnings: normalizeArray(planResponse?.warnings),
    missingDetails: normalizeArray(planResponse?.missing_details),
    candidateAnalysisTypes: normalizeArray(planResponse?.candidate_analysis_types),
    queryPlan: normalizeSpec(planResponse?.query_plan),
    clarifyingQuestion: normalizeSpec(planResponse?.clarifying_question),
    reviewPosture: normalizeSpec(planResponse?.review_posture),
    plannerContext: normalizeSpec(planResponse?.planner_context),
    spec,
    focusTarget: 'review',
  });
};

export const buildRecoveryAnalysisDraft = (recoveryDraft) => {
  const spec = normalizeSpec(recoveryDraft?.spec);
  if (!spec) {
    return null;
  }

  return buildActiveDraftFromSpec({
    sourceType: 'recovery',
    sourceLabel: recoveryDraft.recovery_action_label || 'Recovered analysis draft',
    sourceJobId: recoveryDraft.source_job_id || null,
    planReady: true,
    submitReady: true,
    outcome: 'ready',
    analysisType: recoveryDraft.analysis_type || null,
    instrumentSymbol: recoveryDraft.instrument_symbol || null,
    plannerSource: recoveryDraft?.planning_provenance?.planner_source || 'recovery_action_v1',
    plannerModel: recoveryDraft?.planning_provenance?.planner_model || null,
    assumptions: [
      `Recovered submitted spec from job ${recoveryDraft.source_job_id}.`,
      recoveryDraft.recovery_action_type === 'edit_spec'
        ? 'Edit the recovered JSON before resubmitting the analysis.'
        : 'Recovered JSON is loaded and ready for resubmission.',
    ],
    warnings: [],
    missingDetails: [],
    candidateAnalysisTypes: [],
    spec,
    focusTarget: recoveryDraft.focus_target || 'review',
  });
};

export const buildPersistedAnalysisDraft = (draftResponse) => {
  const spec = normalizeSpec(draftResponse?.analysis_spec);
  if (!spec) {
    return null;
  }

  const plannerContext = normalizeSpec(draftResponse?.planner_context) || {};
  const metadata = normalizeSpec(spec?.metadata) || {};

  return buildActiveDraftFromSpec({
    sourceType: draftResponse?.source_type || 'manual',
    sourceLabel: draftResponse?.name || 'Saved analysis draft',
    sourceJobId: null,
    planReady: Boolean(spec),
    submitReady: Boolean(draftResponse?.submit_ready),
    outcome: plannerContext.outcome || null,
    analysisType: draftResponse?.analysis_type || metadata.analysis_type || null,
    instrumentSymbol: draftResponse?.instrument_symbol || null,
    plannerSource: plannerContext.planner_source || metadata.planner_source || 'draft_persistence_v1',
    plannerModel: plannerContext.planner_model || metadata.planner_model || null,
    assumptions: draftResponse?.assumptions,
    warnings: draftResponse?.planning_warnings,
    missingDetails: draftResponse?.missing_details,
    candidateAnalysisTypes: [],
    queryPlan: plannerContext.query_plan || null,
    clarifyingQuestion: plannerContext.clarifying_question || null,
    reviewPosture: plannerContext.review_posture || null,
    plannerContext,
    spec,
    focusTarget: 'review',
    persistedDraftId: draftResponse?.draft_id || null,
    persistedDraftName: draftResponse?.name || '',
    persistedDraftStatus: draftResponse?.status || 'active',
  });
};